import { useState, ReactNode, useRef, useCallback, useMemo } from "react"
import { definition, styles } from "@uesio/ui"
import {
  autoPlacement,
  useFloating,
  autoUpdate,
  offset as setOffset,
  FloatingPortal,
  useListNavigation,
  useInteractions,
  useDismiss,
  useClick,
  useRole,
  FloatingFocusManager,
  Placement,
} from "@floating-ui/react"

interface MenuButtonUtilityProps<T> {
  itemRenderer: (item: T) => ReactNode
  onSelect?: (item: T) => void
  items: T[]
  getItemKey: (item: T) => string
  onSearch?: (search: string) => void
  searchFilter?: (item: T, search: string) => boolean
  closeOnSelect?: boolean
  open?: boolean
  reference?: Element
  defaultPlacement?: Placement
  offset?: number
}

const StyleDefaults = Object.freeze({
  root: [],
  menu: [],
  menuheader: [],
  menuitem: [],
  highlighted: [],
  searchbox: [],
  itemsarea: [],
})

const NoStyle = Object.freeze(
  Object.fromEntries(Object.keys(StyleDefaults).map((key) => [key, ""])),
)

const ListMenu: definition.UtilityComponent<MenuButtonUtilityProps<unknown>> = (
  props,
) => {
  const {
    defaultPlacement = "bottom-start",
    offset = 2,
    reference,
    onSearch,
  } = props
  const [isOpen, setIsOpen] = useState(props.open || false)
  const [searchText, setSearchText] = useState("")

  const classes = isOpen
    ? styles.useUtilityStyleTokens(StyleDefaults, props, "uesio/io.listmenu")
    : NoStyle

  const getSearchItems = useCallback(
    (text: string) => {
      onSearch?.(text)
      setSearchText(text)
    },
    [onSearch],
  )

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        getSearchItems(searchText)
      }
      setIsOpen(open)
    },
    [getSearchItems, searchText],
  )

  const elements = useMemo(
    () => (reference ? { reference } : undefined),
    [reference],
  )

  const middleware = useMemo(
    () => [
      setOffset(offset),
      autoPlacement({
        allowedPlacements: [
          "top-end",
          "top-start",
          "bottom-start",
          "bottom-end",
        ],
      }),
    ],
    [offset],
  )

  const floating = useFloating({
    open: isOpen,
    onOpenChange,
    placement: defaultPlacement,
    elements,
    middleware,
    whileElementsMounted: autoUpdate,
  })

  const { floatingStyles, refs } = floating
  const { setReference, setFloating } = refs

  // Track the reference element in state so we can read it during render
  // (refs.domReference.current cannot be accessed during render per react-hooks/refs).
  const [referenceEl, setReferenceEl] = useState<Element | null>(null)
  // Combined callback: tells floating-ui about the reference element and captures it in state.
  const setReferenceCallback = useCallback(
    (node: HTMLDivElement | null) => {
      setReference(node)
      setReferenceEl(node)
    },
    [setReference],
  )

  const listRef = useRef<(HTMLDivElement | null)[]>([])

  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const dismiss = useDismiss(floating.context)
  const click = useClick(floating.context)
  const role = useRole(floating.context, { role: "listbox" })

  const listNavigation = useListNavigation(floating.context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnOpen: false,
    focusItemOnHover: false,
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, role, listNavigation],
  )

  const {
    context,
    items,
    itemRenderer,
    onSelect,
    searchFilter,
    getItemKey,
    children,
    closeOnSelect = true,
    id,
  } = props

  return (
    <>
      <div
        className="flex"
        tabIndex={0}
        id={id}
        ref={setReferenceCallback}
        {...getReferenceProps({
          onClick: (e) => {
            e.stopPropagation()
            e.preventDefault()
          },
        })}
      >
        {children}
      </div>

      {isOpen && (
        <FloatingPortal root={styles.getClosestThemeRoot(referenceEl)}>
          <FloatingFocusManager context={floating.context} modal={false}>
            <div
              ref={setFloating}
              style={floatingStyles}
              className={classes.menu}
              {...getFloatingProps()}
            >
              <div id={`floatingMenu-${id}`} className={classes.menuheader}>
                {(onSearch || searchFilter) && (
                  <input
                    type="text"
                    value={searchText}
                    autoFocus
                    className={classes.searchbox}
                    placeholder={`${context.getLabel("uesio/io.search")}...`}
                    onChange={(e) => {
                      getSearchItems(e.target.value)
                    }}
                  />
                )}
              </div>
              <div className={classes.itemsarea}>
                {items
                  .filter((item) => {
                    if (!searchFilter) return true
                    if (!searchText) return true
                    return searchFilter(item, searchText)
                  })
                  .map((item, index) => (
                    <div
                      className={styles.cx(
                        classes.menuitem,
                        activeIndex === index && classes.highlighted,
                      )}
                      key={getItemKey(item)}
                      tabIndex={activeIndex === index ? 0 : -1}
                      ref={(node) => {
                        // Standard floating-ui pattern for collecting list item refs per https://floating-ui.com/docs/useListNavigation
                        listRef.current[index] = node
                      }}
                      aria-selected={activeIndex === index}
                      {...getItemProps({
                        // Handle pointer select.
                        onClick(event) {
                          event.preventDefault()
                          event.stopPropagation()
                          onSelect && onSelect(item)
                          closeOnSelect && setIsOpen(false)
                        },
                        // Handle keyboard select.
                        onKeyDown(event) {
                          if (event.key === "Enter") {
                            event.preventDefault()
                            event.stopPropagation()
                            onSelect && onSelect(item)
                            closeOnSelect && setIsOpen(false)
                          }
                        },
                      })}
                    >
                      {itemRenderer(item)}
                    </div>
                  ))}
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}

export default ListMenu
